import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import ListGroup from 'react-bootstrap/ListGroup'
import Modal from 'react-bootstrap/Modal'
import Row from 'react-bootstrap/Row'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import { applyReceiptAnalysis, type Receipt, type ReceiptAnalysisResult } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { useCurrencies } from '../../hooks/useCurrencies'
import { receiptAnalysesQueryKey, useReceiptAnalyses } from '../../hooks/useReceipts'
import { nazevRozpoznavani } from '../../hooks/useZpracovaniUctenek'
import { formatDateTime } from '../../utils/format'
import { exponentOf, formatMoney } from '../../utils/money'

export type ReceiptAnalysesModalProps = {
    show: boolean
    receipt: Receipt
    onClose: () => void
    /** Účtenka po použití vybraného výsledku. */
    onApplied: (receipt: Receipt) => void
}

/** Kdo účtenku přečetl - do záhlaví sloupce. */
export function kdoPrecetl(vysledek: ReceiptAnalysisResult): string {
    return vysledek.engine === 'MCP' ? 'AI asistent (MCP)' : `Aplikace (${nazevRozpoznavani(vysledek.provider)})`
}

/**
 * Porovnání výsledků rozpoznání účtenky - od aplikace i od AI asistenta,
 * případně z opakovaného čtení. Do položek účtenky se promítá jen jeden;
 * uživatel tu vidí rozdíly a použije ten, který sedí.
 */
export function ReceiptAnalysesModal({ show, receipt, onClose, onApplied }: ReceiptAnalysesModalProps) {
    const queryClient = useQueryClient()
    const { data: currencies } = useCurrencies()
    const { data: vysledky, isLoading } = useReceiptAnalyses(receipt.id, show)
    const [pouziva, setPouziva] = useState<string>()

    const mena = (vysledek: ReceiptAnalysisResult) => vysledek.currencyCode ?? receipt.detectedCurrencyCode ?? 'CZK'
    const castka = (vysledek: ReceiptAnalysisResult, hodnota: number | null | undefined) =>
        hodnota === null || hodnota === undefined ? '—' : formatMoney(hodnota, mena(vysledek), exponentOf(currencies, mena(vysledek)))

    const pouzij = async (vysledek: ReceiptAnalysisResult) => {
        const prirazeno = receipt.items.some((item) => item.memberIds.length > 0)
        if (prirazeno && !window.confirm('Vybraný výsledek nahradí položky účtenky i s přiřazením osob. Pokračovat?')) {
            return
        }

        setPouziva(vysledek.id)
        try {
            const aktualni = await actionApiCall(
                () => applyReceiptAnalysis({ path: { receiptId: receipt.id, analysisId: vysledek.id } }),
                { successMessage: `Použit výsledek: ${kdoPrecetl(vysledek)}.` },
            )
            if (aktualni) {
                await queryClient.invalidateQueries({ queryKey: receiptAnalysesQueryKey(receipt.id) })
                onApplied(aktualni)
                onClose()
            }
        } finally {
            setPouziva(undefined)
        }
    }

    const seznam = vysledky ?? []
    // Řádek, ve kterém se výsledky liší, se zvýrazní - na první pohled je vidět, kde hledat chybu.
    const lisiSe = (hodnota: (vysledek: ReceiptAnalysisResult) => unknown) => new Set(seznam.map((vysledek) => String(hodnota(vysledek)))).size > 1
    const radky: Array<{ nazev: string; hodnota: (vysledek: ReceiptAnalysisResult) => string }> = [
        { nazev: 'Obchod', hodnota: (vysledek) => vysledek.merchant ?? '—' },
        { nazev: 'Datum', hodnota: (vysledek) => (vysledek.issuedAt ? formatDateTime(vysledek.issuedAt) : '—') },
        { nazev: 'Celkem', hodnota: (vysledek) => castka(vysledek, vysledek.total) },
        { nazev: 'Součet položek', hodnota: (vysledek) => castka(vysledek, vysledek.itemsTotal) },
        { nazev: 'Položek', hodnota: (vysledek) => String(vysledek.items.length) },
    ]

    return (
        <Modal show={show} onHide={onClose} centered scrollable size="xl">
            <Modal.Header closeButton>
                <Modal.Title as="h2" className="h5">Porovnání výsledků</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className="text-secondary small">
                    Účtenku přečetlo víc nástrojů. Do položek se promítá jen jeden výsledek — rozdíly jsou zvýrazněné.
                    Použitím jiného výsledku se nahradí položky i s přiřazením osob, název obchodu, částka a datum.
                </p>

                {isLoading && (
                    <div className="text-secondary small">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Načítám výsledky…
                    </div>
                )}

                {seznam.length > 0 && (
                    <>
                        <Table responsive size="sm" className="small align-middle" data-testid="porovnani-vysledku">
                            <thead>
                                <tr>
                                    <th />
                                    {seznam.map((vysledek) => (
                                        <th key={vysledek.id}>
                                            <div>{kdoPrecetl(vysledek)}</div>
                                            <div className="fw-normal text-secondary">{formatDateTime(vysledek.createdAt)}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {radky.map((radek) => (
                                    <tr key={radek.nazev} className={lisiSe(radek.hodnota) ? 'su-rozdil' : undefined}>
                                        <th scope="row" className="fw-semibold text-nowrap">
                                            {radek.nazev}
                                        </th>
                                        {seznam.map((vysledek) => (
                                            <td key={vysledek.id}>{radek.hodnota(vysledek)}</td>
                                        ))}
                                    </tr>
                                ))}
                                <tr>
                                    <th scope="row" />
                                    {seznam.map((vysledek) => (
                                        <td key={vysledek.id}>
                                            <TlacitkoPouzit vysledek={vysledek} pouziva={pouziva} onPouzij={pouzij} />
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        </Table>

                        <Row xs={1} md={Math.min(seznam.length, 3)} className="g-3">
                            {seznam.map((vysledek) => (
                                <Col key={vysledek.id}>
                                    <Card className={vysledek.isApplied ? 'border-success' : undefined} data-testid="vysledek-rozpoznani">
                                        <Card.Header className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="small fw-semibold">{kdoPrecetl(vysledek)}</span>
                                            {vysledek.isApplied && <Badge bg="success">Použito</Badge>}
                                        </Card.Header>
                                        <ListGroup variant="flush">
                                            {vysledek.items.map((item, index) => (
                                                <ListGroup.Item key={index} className="d-flex justify-content-between gap-2 small">
                                                    <span className="text-break">
                                                        {item.quantity !== 1 && <span className="text-secondary">{String(item.quantity).replace('.', ',')}× </span>}
                                                        {item.label}
                                                    </span>
                                                    <span className="text-nowrap">{castka(vysledek, item.totalPrice)}</span>
                                                </ListGroup.Item>
                                            ))}
                                            {vysledek.items.length === 0 && (
                                                <ListGroup.Item className="small text-secondary">Žádné položky.</ListGroup.Item>
                                            )}
                                        </ListGroup>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" className="su-tappable" onClick={onClose}>
                    Zavřít
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

function TlacitkoPouzit({
    vysledek,
    pouziva,
    onPouzij,
}: {
    vysledek: ReceiptAnalysisResult
    pouziva: string | undefined
    onPouzij: (vysledek: ReceiptAnalysisResult) => Promise<void>
}) {
    if (vysledek.isApplied) {
        return <Badge bg="success">Použito</Badge>
    }
    return (
        <Button
            size="sm"
            variant="outline-primary"
            className="su-tappable"
            disabled={!!pouziva}
            aria-label={`Použít výsledek: ${kdoPrecetl(vysledek)}`}
            onClick={() => void onPouzij(vysledek)}
        >
            {pouziva === vysledek.id && <Spinner animation="border" size="sm" className="me-1" />}
            Použít
        </Button>
    )
}
